import {paginatedApiRequest} from './api';
import {adminRequest, readableMessage, requireData} from './adminRequest';
import {BaseRequestPayload} from './api/types/api-request.types';
import {
  adminTokenAbuseEndpoint,
  adminTokenAbuseResolveEndpoint,
  adminTokenChangesEndpoint,
  adminTokenRuleEndpoint,
  adminTokenRulesEndpoint,
  adminTokenSettingsEndpoint,
  adminUserTokensAdjustEndpoint,
  adminUserTokensEndpoint,
  adminUserTokensTrustEndpoint,
} from '../constants/endpoints';

export type TokenRuleKind =
  | 'BookingHonored'
  | 'BookingVerifiedVisit'
  | 'GoingVerifiedVisit'
  | 'NewPlaceVisit'
  | 'Review'
  | 'Mood'
  | 'ReactionReceived'
  | 'TripCompleted'
  | 'FriendAdded'
  | 'ChallengeCompleted'
  | 'AdminAdjustment';

export type TrustLevel = 'Normal' | 'Limited' | 'Blocked';

export interface TokenRule {
  kind: TokenRuleKind;
  points: number;
  // Null means no cap.
  dailyCap: number | null;
  weeklyCap: number | null;
  enabled: boolean;
  updatedAt: string;
}

export interface TokenSettingsValues {
  // The most points a user can earn across every rule, per day / per week.
  dailyCap: number;
  weeklyCap: number;
  // QR and location visits together.
  maxVerifiedVisitsPerDay: number;
  qrWindowBeforeMinutes: number;
  // Counted after the start when the booking has no end.
  qrWindowAfterMinutes: number;
  locationWindowBeforeMinutes: number;
  // Used when there is no end.
  locationWindowAfterMinutes: number;
  // Pings inside the activity's cell.
  locationMinPings: number;
  locationMinSpanMinutes: number;
  // Faster flags the user and voids the day.
  maxSpeedKmh: number;
}

export interface TokenSettings extends TokenSettingsValues {
  updatedAt: string;
}

export interface TokenRules {
  rules: TokenRule[];
  settings: TokenSettings;
}

export interface UpdateTokenRuleInput {
  points: number;
  dailyCap: number | null;
  weeklyCap: number | null;
  enabled: boolean;
}

// Every field is optional: an absent field stays unchanged.
export type UpdateTokenSettingsInput = Partial<TokenSettingsValues>;

export interface TokenConfigChange {
  id: number;
  adminUserId: string | null;
  adminName: string | null;
  target: string;
  // Null when the target was created by this change.
  beforeJson: string | null;
  afterJson: string;
  createdAt: string;
}

export interface TokenConfigChangesPage {
  items: TokenConfigChange[];
  totalItems: number;
  maxPage: number;
}

export interface TokenLedgerEntry {
  id: number;
  rule: TokenRuleKind | string;
  points: number;
  sourceKind: string;
  sourceId: string | null;
  isReversal: boolean;
  note: string | null;
  createdAt: string;
}

export interface TokenLedgerPage {
  items: TokenLedgerEntry[];
  totalCount: number;
  pageSize: number;
  pageNumber: number;
  totalPages: number;
}

export interface UserTokens {
  userId: string;
  displayName: string;
  // Null when the account has no email.
  email: string | null;
  balance: number;
  lifetimePoints: number;
  earnedToday: number;
  earnedThisWeek: number;
  trustLevel: TrustLevel;
  ledger: TokenLedgerPage;
}

export interface AdjustUserTokensInput {
  // Never zero; negative takes points away.
  points: number;
  note: string;
}

export interface SetUserTrustInput {
  trustLevel: TrustLevel;
  note?: string;
}

export const TOKEN_PAGE_SIZE = 20;

// What the API's error codes mean to an admin. adminRequest surfaces the
// server's message as is, which for these failures is the bare code.
const ERROR_MESSAGES: Record<string, string> = {
  invalid_rule:
    'Check the rule: points 0-10 000, caps whole numbers of 0 or more.',
  invalid_settings:
    'Check the settings: every value must be inside its range, and the weekly cap at least the daily cap.',
  invalid_adjustment:
    'Check the adjustment: points other than 0 and at most 10 000 either way, with a note of up to 450 characters.',
  balance_below_zero: 'The balance cannot go below zero.',
  not_found: 'The user, rule or flag was not found.',
  invalid_trust_level: 'The trust level is not valid.',
};

// adminRequest with the known error codes turned into readable messages.
const tokenRequest = async <TResponse, TBody = undefined>(
  payload: BaseRequestPayload<TBody>,
  failureMessage: string,
): Promise<TResponse | null> => {
  try {
    return await adminRequest<TResponse, TBody>(payload, failureMessage);
  } catch (error) {
    const message = error instanceof Error ? error.message : '';
    throw new Error(ERROR_MESSAGES[message] ?? (message || failureMessage));
  }
};

const emptyChangesPage: TokenConfigChangesPage = {
  items: [],
  totalItems: 0,
  maxPage: 0,
};

export const fetchTokenRules = async (): Promise<TokenRules> =>
  requireData(
    await tokenRequest<TokenRules>(
      {endpoint: adminTokenRulesEndpoint, method: 'GET'},
      'Failed to fetch the token rules',
    ),
    'Failed to fetch the token rules',
  );

export const updateTokenRule = async (
  kind: TokenRuleKind,
  input: UpdateTokenRuleInput,
): Promise<TokenRule> =>
  requireData(
    await tokenRequest<TokenRule, UpdateTokenRuleInput>(
      {endpoint: adminTokenRuleEndpoint(kind), method: 'PUT', body: input},
      'Failed to update the rule',
    ),
    'Failed to update the rule',
  );

export const updateTokenSettings = async (
  input: UpdateTokenSettingsInput,
): Promise<TokenSettings> =>
  requireData(
    await tokenRequest<TokenSettings, UpdateTokenSettingsInput>(
      {endpoint: adminTokenSettingsEndpoint, method: 'PUT', body: input},
      'Failed to update the caps',
    ),
    'Failed to update the caps',
  );

export const fetchTokenChanges = async (
  pageNumber: number,
): Promise<TokenConfigChangesPage> => {
  const message = 'Failed to fetch the change log';
  let failure: Error | null = null;

  const response = await paginatedApiRequest<TokenConfigChange[]>(
    {
      endpoint: adminTokenChangesEndpoint,
      method: 'GET',
      queryParams: {pageNumber, pageSize: TOKEN_PAGE_SIZE},
    },
    {
      errorMessage: message,
      onError: (error: unknown) => {
        failure = new Error(readableMessage(error, message));
      },
    },
  );

  if (failure) {
    throw failure;
  }

  // An empty result does not come back as a paginated payload.
  if (!response) {
    return emptyChangesPage;
  }

  return {
    items: response.data ?? [],
    totalItems: response.totalItems,
    maxPage: response.maxPage,
  };
};

export const fetchUserTokens = async (
  userId: string,
  pageNumber: number,
): Promise<UserTokens> =>
  requireData(
    await tokenRequest<UserTokens>(
      {
        endpoint: adminUserTokensEndpoint(userId),
        method: 'GET',
        queryParams: {pageNumber, pageSize: TOKEN_PAGE_SIZE},
      },
      'Failed to load the user',
    ),
    'Failed to load the user',
  );

export const adjustUserTokens = async (
  userId: string,
  input: AdjustUserTokensInput,
): Promise<TokenLedgerEntry> =>
  requireData(
    await tokenRequest<TokenLedgerEntry, AdjustUserTokensInput>(
      {
        endpoint: adminUserTokensAdjustEndpoint(userId),
        method: 'POST',
        body: input,
      },
      'Failed to adjust the balance',
    ),
    'Failed to adjust the balance',
  );

export type AbuseReason =
  | 'impossible_speed'
  | 'mock_location'
  | 'too_many_visits'
  | 'qr_outside_window'
  | string;

export interface AbuseFlag {
  id: number;
  userId: string;
  displayName: string;
  reason: AbuseReason;
  details: string | null;
  trustLevel: TrustLevel;
  createdAt: string;
  // Null while the flag is open.
  resolvedAt: string | null;
}

export interface AbuseFlagsPage {
  items: AbuseFlag[];
  totalItems: number;
  maxPage: number;
}

const emptyAbusePage: AbuseFlagsPage = {items: [], totalItems: 0, maxPage: 0};

export const fetchAbuseFlags = async ({
  open,
  page,
}: {
  // True lists only the unresolved flags.
  open: boolean;
  page: number;
}): Promise<AbuseFlagsPage> => {
  const message = 'Failed to fetch the abuse flags';
  let failure: Error | null = null;

  const response = await paginatedApiRequest<AbuseFlag[]>(
    {
      endpoint: adminTokenAbuseEndpoint,
      method: 'GET',
      queryParams: {open, pageNumber: page, pageSize: TOKEN_PAGE_SIZE},
    },
    {
      errorMessage: message,
      onError: (error: unknown) => {
        failure = new Error(readableMessage(error, message));
      },
    },
  );

  if (failure) {
    throw failure;
  }

  // An empty result does not come back as a paginated payload.
  if (!response) {
    return emptyAbusePage;
  }

  return {
    items: response.data ?? [],
    totalItems: response.totalItems,
    maxPage: response.maxPage,
  };
};

// Answers 204, so there is nothing to return.
export const resolveAbuseFlag = async (id: number): Promise<void> => {
  await tokenRequest<void>(
    {endpoint: adminTokenAbuseResolveEndpoint(id), method: 'POST'},
    'Failed to resolve the flag',
  );
};

// Answers 204, so there is nothing to return.
export const setUserTrust = async (
  userId: string,
  input: SetUserTrustInput,
): Promise<void> => {
  await tokenRequest<void, SetUserTrustInput>(
    {
      endpoint: adminUserTokensTrustEndpoint(userId),
      method: 'PUT',
      body: input,
    },
    'Failed to change the trust level',
  );
};
